import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import PulseHeader from '../components/PulseHeader';
import PulseLogo from '../components/PulseLogo';
import PasswordInput from '../components/PasswordInput';
import { toast } from 'sonner';
import { signIn } from '@/services/auth';
import { useAuth } from '@/contexts/AuthContext';
import { usePlatformDetection } from '@/utils/platform-utils.tsx';
import { Loader2 } from 'lucide-react';
import { Apple } from 'lucide-react';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signInWithGoogle, signInWithApple } = useAuth();
  const { isIOS, isAndroid } = usePlatformDetection();
  
  // Get the intended destination from location state, query param, or default to home
  const queryParams = new URLSearchParams(location.search);
  const returnTo = queryParams.get('returnTo') || 
    (location.state as { from?: { pathname: string } })?.from?.pathname || '/';
    
  // Debug logging
  useEffect(() => {
    console.log('Login component - returnTo value:', returnTo);
    console.log('Login component - returnTo type:', typeof returnTo);
    console.log('Login component - search params:', location.search);
    console.log('Login component - location state:', location.state);
    
    // Check if returnTo starts with /chapter/ to ensure proper chapter redirection
    if (returnTo && returnTo.includes('/chapter/')) {
      console.log('Login component - Chapter redirect detected. Chapter ID:', 
        returnTo.replace('/chapter/', ''));
      
      // Check for any double encoding issues
      try {
        const doubleDecoded = decodeURIComponent(decodeURIComponent(returnTo));
        if (doubleDecoded !== returnTo) {
          console.log('Login component - Possible double encoding detected');
          console.log('Login component - Double decoded value:', doubleDecoded);
        }
      } catch (e) {
        console.error('Login component - Error when attempting double decode:', e);
      }
    }
  }, [returnTo, location]);

  // Helper function to safely decode returnTo
  const getDecodedReturnPath = (path: string) => {
    try {
      // Ensure we decode the path properly
      const decodedPath = decodeURIComponent(path);
      console.log('Login component - Decoded path:', decodedPath);
      
      // Validate the path format
      if (!decodedPath.startsWith('/')) {
        // If somehow we got a malformed path, prepend slash
        console.log('Login component - Adding missing slash to path');
        return '/' + decodedPath;
      }
      
      return decodedPath;
    } catch (e) {
      console.error('Login component - Error decoding returnTo:', e);
      // Fallback to home page if decoding fails
      return '/';
    }
  };

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      console.log('Login component - User logged in, redirecting to:', returnTo);
      
      // Check if returnTo is properly decoded
      const decodedReturnTo = getDecodedReturnPath(returnTo);
      console.log('Login component - Prepared path for navigation:', decodedReturnTo);
      
      // Use a timeout to ensure auth context is fully updated
      setTimeout(() => {
        navigate(decodedReturnTo, { replace: true });
      }, 500); // Increased timeout to ensure auth is fully processed
    }
  }, [user, navigate, returnTo]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!email || !password) {
      toast.error('Por favor, preencha todos os campos');
      return;
    }
    
    setLoading(true);
    
    try {
      const { user, error } = await signIn(email, password);
      
      if (error) {
        toast.error(error);
      } else if (user) {
        console.log('Login successful, user will be redirected to:', returnTo);
        
        // Ensure returnTo is properly decoded
        const decodedReturnTo = getDecodedReturnPath(returnTo);
        console.log('Login component - Prepared path for manual navigation:', decodedReturnTo);
        
        toast.success('Login realizado com sucesso!');
        
        // Try immediate navigation, the useEffect should also catch this as a backup
        setTimeout(() => {
          navigate(decodedReturnTo, { replace: true });
        }, 800);  // Longer delay to ensure auth state is fully updated
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Ocorreu um erro durante o login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-full-page-container bg-gradient-to-br from-rose-700 via-pink-700 to-purple-700 text-white">
      
      <div className="flex-1 flex flex-col items-center justify-center p-6 text-center pb-16">
        <PulseLogo size="lg" variant="white" />
        <p className="mt-2 mb-8">Seu app de romances</p>
        
        <form onSubmit={handleLogin} className="w-full space-y-6">
          <div className="space-y-2 text-left">
            <label htmlFor="email" className="block">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Insira o email"
              className="auth-input"
            />
          </div>
          
          <div className="space-y-2 text-left">
            <label htmlFor="password" className="block">Senha</label>
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <div className="text-right">
              <Link to="/reset-password" className="text-sm pulse-link">
                Esqueceu sua senha?
              </Link>
            </div>
          </div>
          
          <button 
            type="submit" 
            className="pulse-btn-primary mt-8"
            disabled={loading || googleLoading || appleLoading}
          >
            {loading ? 'Entrando...' : 'Login com E-Mail e Senha'}
          </button>
        </form>
        
        {/* <div className="w-full max-w-sm">
          {isIOS ? null : (
            <>
              <button
                type="button"
                onClick={async () => {
                  setAppleLoading(true);
                  try {
                    await signInWithApple();
                    // The AuthContext's onAuthStateChange should handle redirection
                  } catch (error) {
                    toast.error('Falha ao tentar login com Apple.');
                    console.error('Apple sign-in error on Login page:', error);
                  } finally {
                    setAppleLoading(false);
                  }
                }}
                className="pulse-btn-social w-full hidden"
                disabled={loading || googleLoading || appleLoading}
              >
                {appleLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Apple className="mr-2 h-5 w-5" />
                )}
                Entrar com Apple
              </button>

              <div className="my-4 flex items-center">
                <div className="flex-grow border-t border-white/30"></div>
                <span className="px-4 text-white/80">OU</span>
                <div className="flex-grow border-t border-white/30"></div>
              </div>
            </>
          )}
        </div>

        {!isIOS && (
          <button
            type="button"
            onClick={async () => {
              setGoogleLoading(true);
              try {
                await signInWithGoogle();
                // The AuthContext's onAuthStateChange should handle redirection
              } catch (error) {
                toast.error('Falha ao tentar login com Google.');
                console.error('Google sign-in error on Login page:', error);
              } finally {
                setGoogleLoading(false);
              }
            }}
            className="pulse-btn-primary w-full max-w-sm mt-4"
            disabled={loading || googleLoading || appleLoading}
          >
            {googleLoading ? (
              'Redirecionando...'
            ) : (
              <span className="flex items-center justify-center">
                <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className="w-5 h-5 mr-2">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                  <path fill="none" d="M0 0h48v48H0z"></path>
                </svg>
                Login com Google
              </span>
            )}
          </button>
        )} */}
        
        <p className="mt-8">
          Não tem uma conta?{' '}
          <Link 
            to={`/signup${returnTo !== '/' ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`} 
            className="pulse-link"
          >
            Cadastre-se aqui
          </Link>
        </p>
        <p className="mt-4 text-xs text-white/80">
         Ao entrar, você concorda com nossos <Link to="/terms-of-service" className="pulse-link underline">Termos de Serviço</Link> e <Link to="/privacy-policy" className="pulse-link underline">Política de Privacidade</Link>.
       </p>
      </div>
    </div>
  );
};

export default Login;
